/**
 * Chart Export Utilities
 * Provides functions to export charts as SVG, PNG, and CSV
 */
import { csvBlob } from './fileDownload'

/**
 * Export chart as SVG file
 * @param chartRef - Reference to the chart container element
 * @param filename - Name for the downloaded file (without extension)
 */
export const exportToSVG = (chartRef: HTMLElement | null, filename: string = 'chart') => {
  if (!chartRef) {
    console.error('Chart reference is null')
    return
  }

  const svg = chartRef.querySelector('svg')
  if (!svg) {
    console.error('No SVG element found in chart')
    return
  }

  // Clone the SVG to avoid modifying the original
  const clonedSvg = svg.cloneNode(true) as SVGElement

  // Add XML namespace if not present
  if (!clonedSvg.getAttribute('xmlns')) {
    clonedSvg.setAttribute('xmlns', 'http://www.w3.org/2000/svg')
  }

  // Get computed styles and inline them for better export
  const styleElement = document.createElement('style')
  styleElement.textContent = `
    text { font-family: system-ui, -apple-system, sans-serif; }
    .recharts-cartesian-grid-horizontal line,
    .recharts-cartesian-grid-vertical line { stroke: #e5e7eb; }
  `
  clonedSvg.insertBefore(styleElement, clonedSvg.firstChild)

  // Serialize and download
  const serializer = new XMLSerializer()
  const svgString = serializer.serializeToString(clonedSvg)
  const blob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' })

  downloadBlob(blob, `${filename}.svg`)
}

/**
 * Export chart as PNG file
 * Uses canvas to render SVG as PNG
 * @param chartRef - Reference to the chart container element
 * @param filename - Name for the downloaded file (without extension)
 * @param scale - Scale factor for higher resolution (default: 2)
 */
export const exportToPNG = async (
  chartRef: HTMLElement | null,
  filename: string = 'chart',
  scale: number = 2
) => {
  if (!chartRef) {
    console.error('Chart reference is null')
    return
  }

  const svg = chartRef.querySelector('svg')
  if (!svg) {
    console.error('No SVG element found in chart')
    return
  }

  // Get SVG dimensions
  const svgRect = svg.getBoundingClientRect()
  const width = svgRect.width * scale
  const height = svgRect.height * scale

  // Clone and prepare SVG
  const clonedSvg = svg.cloneNode(true) as SVGElement
  clonedSvg.setAttribute('width', String(svgRect.width))
  clonedSvg.setAttribute('height', String(svgRect.height))

  if (!clonedSvg.getAttribute('xmlns')) {
    clonedSvg.setAttribute('xmlns', 'http://www.w3.org/2000/svg')
  }

  // Inline styles for proper rendering
  const styleElement = document.createElement('style')
  styleElement.textContent = `
    text { font-family: system-ui, -apple-system, sans-serif; fill: #374151; }
    .recharts-cartesian-grid-horizontal line,
    .recharts-cartesian-grid-vertical line { stroke: #e5e7eb; }
  `
  clonedSvg.insertBefore(styleElement, clonedSvg.firstChild)

  // Convert SVG to data URL
  const serializer = new XMLSerializer()
  const svgString = serializer.serializeToString(clonedSvg)
  const svgDataUrl = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svgString)

  // Create canvas and draw
  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height
  const ctx = canvas.getContext('2d')

  if (!ctx) {
    console.error('Could not get canvas context')
    return
  }

  // Set white background
  ctx.fillStyle = '#ffffff'
  ctx.fillRect(0, 0, width, height)

  // Load image and draw
  return new Promise<void>((resolve, reject) => {
    const img = new Image()
    img.onload = () => {
      ctx.drawImage(img, 0, 0, width, height)
      canvas.toBlob((blob) => {
        if (blob) {
          downloadBlob(blob, `${filename}.png`)
          resolve()
        } else {
          reject(new Error('Failed to create PNG blob'))
        }
      }, 'image/png')
    }
    img.onerror = () => reject(new Error('Failed to load SVG as image'))
    img.src = svgDataUrl
  })
}

/**
 * Export data as CSV file
 * @param data - Array of objects to export
 * @param filename - Name for the downloaded file (without extension)
 * @param columns - Optional column configuration { key: 'dataKey', header: 'Display Name' }
 */
export const exportToCSV = <T extends Record<string, unknown>>(
  data: T[],
  filename: string = 'data',
  columns?: Array<{ key: keyof T; header: string }>
) => {
  if (!data || data.length === 0) {
    console.error('No data to export')
    return
  }

  let headers: string[]
  let keys: (keyof T)[]

  if (columns) {
    headers = columns.map((col) => col.header)
    keys = columns.map((col) => col.key)
  } else {
    // Auto-detect columns from first row
    keys = Object.keys(data[0]) as (keyof T)[]
    headers = keys.map((key) => formatHeader(String(key)))
  }

  // Build CSV content
  const rows: string[] = []

  // Header row
  rows.push(headers.map(escapeCSVValue).join(','))

  // Data rows
  for (const row of data) {
    const values = keys.map((key) => {
      const value = row[key]
      return escapeCSVValue(formatCSVValue(value))
    })
    rows.push(values.join(','))
  }

  const csvContent = rows.join('\n')
  // CR-8703: led by a UTF-8 BOM, or Excel garbles every en dash and accent.
  const blob = csvBlob(csvContent)

  downloadBlob(blob, `${filename}.csv`)
}

/**
 * Format header from camelCase or snake_case to Title Case
 */
const formatHeader = (key: string): string => {
  return key
    .replace(/([A-Z])/g, ' $1') // camelCase
    .replace(/_/g, ' ') // snake_case
    .replace(/^\w/, (c) => c.toUpperCase()) // Capitalize first
    .trim()
}

/**
 * Format value for CSV export
 */
const formatCSVValue = (value: unknown): string => {
  if (value === null || value === undefined) {
    return ''
  }
  if (typeof value === 'number') {
    return String(value)
  }
  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No'
  }
  if (value instanceof Date) {
    return value.toISOString().split('T')[0]
  }
  return String(value)
}

/**
 * Escape CSV value (handle commas, quotes, newlines)
 */
const escapeCSVValue = (value: string): string => {
  if (value.includes(',') || value.includes('"') || value.includes('\n')) {
    return `"${value.replace(/"/g, '""')}"`
  }
  return value
}

/**
 * Helper to download a blob as a file
 */
const downloadBlob = (blob: Blob, filename: string) => {
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}
